import axios, { AxiosError } from "axios";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { LocationSummary } from "../model/ShiftLocation";
import { HttpClientError } from "../../../../providers/HttpClient";
import { invalidateAttendanceViews } from "./attendanceCache";

interface ShiftLocationUpdateData {
  latitude?: number;
  longitude?: number;
  radius_allowance?: number;
  late_arrival_threshold_minutes?: number | null;
}

interface UpdateShiftLocationRequest {
  id: string;
  data: ShiftLocationUpdateData;
}

const UpdateShiftLocationApiService = async (
  request: UpdateShiftLocationRequest
): Promise<LocationSummary> => {
  try {
    const { data } = await axios.patch(
      `/shift-locations/${request.id}/`,
      request.data
    );
    return data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      HttpClientError(error);
    }
    throw error;
  }
};

const GetShiftLocationApiService = async (
  id: string
): Promise<LocationSummary> => {
  try {
    const { data } = await axios.get(`/shift-locations/${id}/`);
    return data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      HttpClientError(error);
    }
    throw error;
  }
};

const useGetShiftLocationApiService = (request: {
  id: string;
  enabled?: boolean;
}): ReturnType<typeof useQuery<LocationSummary, AxiosError>> =>
  useQuery<LocationSummary, AxiosError>(
    ["GetShiftLocationApiService", request.id],
    () => GetShiftLocationApiService(request.id),
    {
      enabled: request.enabled !== false && Boolean(request.id),
      keepPreviousData: true,
    }
  );

const useUpdateShiftLocationApiService = (): ReturnType<
  typeof useMutation<LocationSummary, AxiosError, UpdateShiftLocationRequest>
> => {
  const queryClient = useQueryClient();
  return useMutation<LocationSummary, AxiosError, UpdateShiftLocationRequest>(
    (request) => UpdateShiftLocationApiService(request),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["GetShiftLocationApiService"]);
        invalidateAttendanceViews(queryClient);
      },
    }
  );
};

export { useGetShiftLocationApiService, useUpdateShiftLocationApiService };
